import { Link } from 'react-router-dom'
import { Card } from '../common/Card'
import type { Hunt } from '../../types'
import { formatDistance } from '../../hooks/useGeolocation'

interface HuntCardProps {
  hunt: Hunt
  isActive?: boolean
}

const difficultyBadges = {
  easy: 'badge-easy',
  medium: 'badge-medium',
  hard: 'badge-hard',
}

export default function HuntCard({ hunt, isActive }: HuntCardProps) {
  return (
    <Link to={`/hunts/${hunt.id}`}>
      <Card variant="interactive" padding="none" className="relative">
        {/* Active hunt indicator */}
        {isActive && (
          <div className="absolute top-3 right-3 z-10">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-error-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-error-500"></span>
            </span>
          </div>
        )}

        {hunt.cover_image_url && (
          <img
            src={hunt.cover_image_url}
            alt={hunt.title}
            className="w-full h-32 object-cover"
          />
        )}
        <div className="p-4">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-brown-900 line-clamp-1">
              {hunt.title}
            </h3>
            <span className={`badge ${difficultyBadges[hunt.difficulty]}`}>
              {hunt.difficulty}
            </span>
          </div>

          <p className="text-brown-600 text-sm mt-1 line-clamp-2">
            {hunt.description}
          </p>

          {hunt.featured_reward && (() => {
            const fr = hunt.featured_reward
            const isPool = fr.total_quantity != null
            const remaining = fr.remaining ?? 0
            const exhausted = isPool && remaining <= 0
            return (
              <div
                className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
                  exhausted
                    ? 'bg-stone-100 text-stone-500'
                    : 'bg-amber-100 text-amber-800'
                }`}
              >
                <span>🎁</span>
                {isPool ? (
                  exhausted ? (
                    <span>Reward pool claimed</span>
                  ) : (
                    <span>
                      {remaining} of {fr.total_quantity} left
                    </span>
                  )
                ) : (
                  <span>{fr.name}</span>
                )}
              </div>
            )
          })()}

          <div className="flex items-center gap-4 mt-3 text-sm text-brown-500">
            <span className="flex items-center gap-1.5">
              <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {hunt.waypoint_count} stops
            </span>

            {hunt.estimated_duration_minutes && (
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                ~{hunt.estimated_duration_minutes} min
              </span>
            )}

            {hunt.distance_meters !== undefined && hunt.distance_meters > 0 && (
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                {formatDistance(hunt.distance_meters)} away
              </span>
            )}
          </div>
        </div>
      </Card>
    </Link>
  )
}
